import React, {useEffect, useState} from "react";
import {Settings} from "../../../models/models";
import {PAYMENT_TYPE, SUBSCRIPTION_ROOT_PAGES, VDS_LEVEL, VDS_PAGES} from "../../../constants";
import Loading from "../../common/Loading";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import {
    cancelPaymentIntent,
    cancelSetupIntent,
    createCustomerForAccount,
    createPaymentIntent, createSetupIntent, setVDSDoNotRenew, updateVDSSubscription
} from "../../../api/requests";
import useBeforeUnload from "../../../helpers/useBeforeUnload";
import {
    daysBetween, getPreferredVDSPaymentMethod,
    getSameVDSTierPlanAtDifferentRecurrence,
    setSelectedPlanAsOriginal
} from "../../../helpers/helpers";
import PlanHeaderNotes from "../../common/subscriptions/PlanHeaderNotes";
import ManagePage from "../common/ManagePage";
import UpdateSubscriptionPage from "../common/UpdateSubscriptionPage";
import CheckoutPage from "../common/CheckoutPage";
import UpdateSubscriptionConfirmPage from "../common/UpdateSubscriptionConfirmPage";
import SplashPage from "../common/SplashPage";
import PlanSelectPage from "../common/PlanSelectPage";

const VdsContainer = (props: {
    setData: Function, settings: Settings,
    handleError: Function, setServerResponse: Function, rootPage: number, setRootPage: Function,
    setShouldRefresh: Function, preferredVDSPaymentMethod: Object, isDemoReseller: boolean
}) => {
    const {
        appData,
        data,
        setData,
        settings,
        handleError,
        setServerResponse,
        rootPage,
        setRootPage,
        setShouldRefresh,
        preferredVDSPaymentMethod,
        isDemoReseller
    } = props;

    if (!data || !settings || rootPage !== SUBSCRIPTION_ROOT_PAGES.VDS || isDemoReseller) {
        return <></>;
    }

    const {
        level, levels, expirationDate,
        customerId, isEligibleForFreeTrial
    } = data;
    const {publishableKey} = settings;

    const {allPaymentMethods, isActiveDemoResellerAccount, isExpiredDemoResellerAccount } = appData;
    if (isActiveDemoResellerAccount || isExpiredDemoResellerAccount)
        return <></>;

    const daysUntilExpired = daysBetween(new Date(), new Date(expirationDate));
    const hasExpired = daysUntilExpired < 0;

    const [page, setPage] = useState(VDS_PAGES.SPLASH);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clientSecretKey, setClientSecretKey] = useState(""); // Unique checkout session key for customer.
    const [payNow, setPayNow] = useState(null);

    // A PaymentIntent is used to keep track of the payment flow and is created upon
    // React app load: https://stripe.com/docs/payments/payment-intents
    const [paymentIntentId, setPaymentIntentId] = useState("");

    useEffect(() => {
        if (page === VDS_PAGES.PLAN_SELECT && !customerId) {
            createCustomer();
        } else if (page === VDS_PAGES.CHECKOUT) {
            if (!publishableKey || !selectedPlan) {
                handleError(i18n.payments.error.genericTitle, i18n.payments.error.failedToLoadCheckoutForm, setSelectedPlan);
                return;
            } else if (!customerId) {
                createCustomer(() => {
                    createIntent();
                });
                return;
            }

            createIntent();
        }
    }, [page]);

    const createCustomer = (callback) => {
        setLoading(true);
        createCustomerForAccount((result) => {
            setLoading(false);
            if (result.status !== 200) {
                console.error('Failed to create customer for account');
                handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                return;
            }

            setData({
                ...data,
                customerId: result.data.customerId,
            });

            if (callback) {
                callback();
            }
        });
    };

    const createIntent = () => {
        setLoading(true);
        setPaymentIntentId("");

        if (payNow) {
            createPaymentIntent(selectedPlan.productId, PAYMENT_TYPE.VDS, (result) => {
                setLoading(false);
                if (result.status !== 200) {
                    console.error('Failed to create payment intent');
                    handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                    return;
                }
                setPaymentIntentId(result.data.id);
                setClientSecretKey(result.data.clientSecret);
            });
        } else {
            createSetupIntent(selectedPlan.productId, PAYMENT_TYPE.VDS, (result) => {
                setLoading(false);
                if (result.status !== 200) {
                    console.error('Failed to create setup intent');
                    handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                    return;
                }
                setPaymentIntentId(result.data.id);
                setClientSecretKey(result.data.clientSecret);
            });
        }
    };

    useEffect(() => {
        if (payNow === null) {
            return;
        }

        if (!payNow && level !== undefined) {
            setSelectedPlanAsOriginal(levels, level, setSelectedPlan);
        }
    }, [payNow]);

    // Clean Up //////////////////////////////////////////////////////////////
    const handlePreventDefault = (event) => {
        event.preventDefault(); // Cancel the event as stated by the standard.
        event.returnValue = ''; // Chrome requires returnValue to be set.
    }

    const handleLeavingPage = (event) => {
        // Cancel any payment attempts that have not yet been processed.
        if (page === VDS_PAGES.CHECKOUT && paymentIntentId) {
            handlePreventDefault(event);

            if (paymentIntentId.includes('seti')) {
                cancelSetupIntent(paymentIntentId, (result) => {
                });
            } else {
                cancelPaymentIntent(paymentIntentId, (result) => {
                });
            }
        }
    };

    // Custom hook to handle applying the 'beforeunload' event listener smoothly.
    useBeforeUnload(handleLeavingPage);

    // UI ///////////////////////////////////////////////////////////////////////////
    let Body = <></>;
    if (loading) {
        Body = (<Loading show={true}/>);
    } else if (page === VDS_PAGES.SPLASH) {
        Body = (
            <SplashPage
                data={data}
                setPage={setPage}
                setPayNow={setPayNow}
                setRootPage={setRootPage}
                pages={VDS_PAGES}
                paymentType={PAYMENT_TYPE.VDS}
                levelType={VDS_LEVEL}
            />
        )
    } else if (page === VDS_PAGES.PLAN_SELECT) {
        Body = (
            <PlanSelectPage
                data={data}
                setData={setData}
                settings={settings}
                payNow={payNow}
                setPage={setPage}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                isEligibleForFreeTrial={isEligibleForFreeTrial}
                pages={VDS_PAGES}
                paymentType={PAYMENT_TYPE.VDS}
            />
        );
    } else if (page === VDS_PAGES.CHECKOUT && clientSecretKey) {
        Body = (
            <Elements stripe={loadStripe(publishableKey)}
                      options={{clientSecret: clientSecretKey}}>
                <CheckoutPage
                    data={data}
                    selectedPlan={selectedPlan}
                    isEligibleForFreeTrial={isEligibleForFreeTrial}
                    paymentIntentId={paymentIntentId}
                    handleError={handleError}
                    payNow={payNow}
                    setServerResponse={setServerResponse}
                    setPage={setPage}
                    preferredPaymentMethodOrNull={getPreferredVDSPaymentMethod(allPaymentMethods)?.paymentMethodId || null}
                    paymentType={PAYMENT_TYPE.VDS}
                    pages={VDS_PAGES}
                />
            </Elements>
        );
    } else if (page === VDS_PAGES.MANAGE) {
        Body = (
            <ManagePage
                data={data}
                pages={VDS_PAGES}
                setPage={setPage}
                setPayNow={setPayNow}
                setLoading={setLoading}
                setServerResponse={setServerResponse}
                setShouldRefresh={setShouldRefresh}
                preferredPaymentMethod={preferredVDSPaymentMethod}
                paymentType={PAYMENT_TYPE.VDS}
                hasSubscription={true}
                setDoNotRenew={setVDSDoNotRenew}
            />
        );
    } else if (page === VDS_PAGES.UPDATE_SUBSCRIPTION) {
        Body = (
            <UpdateSubscriptionPage
                pages={VDS_PAGES}
                data={data}
                setData={setData}
                payNow={payNow}
                setPage={setPage}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                getTierPlanAtDifferentRecurrence={getSameVDSTierPlanAtDifferentRecurrence}
                paymentType={PAYMENT_TYPE.VDS}/>
        );
    } else if (page === VDS_PAGES.UPDATE_SUBSCRIPTION_CONFIRM) {
        Body = (
            <UpdateSubscriptionConfirmPage
                data={data}
                selectedPlan={selectedPlan}
                handleError={handleError}
                setServerResponse={setServerResponse}
                setPage={setPage}
                setSelectedPlan={setSelectedPlan}
                getSameTierPlanAtDifferentRecurrence={getSameVDSTierPlanAtDifferentRecurrence}
                pages={VDS_PAGES}
                updateSubscription={updateVDSSubscription}
            />
        );
    }

    return (
        <div className={`plan-container ${hasExpired && 'plan-container-expired'}`} data-testid={'vdsContainer'}>
            <PlanHeaderNotes title={i18n.payments.vds.title}
                             subtitle={i18n.payments.vds.productNote}
                             testid={'vdsPlanHeaderNotesDiv'}
                             selectedPlan={selectedPlan}
                             paymentType={PAYMENT_TYPE.VDS}
                             data={data}/>
            {Body}
        </div>
    );
}

export default VdsContainer;